import React from "react";
import { createPortal } from "react-dom";
import { Select as AntSelect, Input as AntInput, ConfigProvider } from "antd";
import type { SelectProps as AntSelectProps } from "antd";
import { Search, ChevronDown, X, Info, Check } from "lucide-react";
import { Input } from "../input/Input";
import { cn } from "@/lib/utils";
import { colors } from "../../theme/colors";
import { Checkbox } from "../checkbox/Checkbox";
import {
  Tooltip,
  type TooltipPlacement,
  type TooltipVariant,
} from "../tool-tip";

// Custom scrollbar styles for dropdowns
const scrollbarStyles = `
  /* Prevent multi-select tags from wrapping */
  .ds-select-nowrap .ant-select-selector {
    flex-wrap: nowrap !important;
    overflow: hidden !important;
  }
  .ds-select-nowrap .ant-select-selection-overflow {
    flex-wrap: nowrap !important;
    overflow: hidden !important;
  }
  .ds-select-nowrap .ant-select-selection-overflow-item {
    flex-shrink: 0 !important;
  }
  .ds-select-nowrap .ant-select-selection-item {
    max-width: 100px !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }
  .ds-select-nowrap .ant-select-selection-item-content {
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }

  /* Allow multi-select tags to wrap */
  .ds-select-wrap .ant-select-selector {
    flex-wrap: wrap !important;
    height: auto !important;
    min-height: 40px !important;
    padding: 4px 8px !important;
  }
  .ds-select-wrap .ant-select-selection-overflow {
    flex-wrap: wrap !important;
    gap: 4px !important;
  }
  .ds-select-wrap .ant-select-selection-overflow-item {
    flex-shrink: 0 !important;
  }
  .ds-select-wrap .ant-select-selection-item {
    margin: 0 !important;
    padding: 2px 8px !important;
    border: 1px solid ${colors.surfaceDark} !important;
    border-radius: 3px !important;
    background: ${colors.white} !important;
    height: auto !important;
    line-height: 1.4 !important;
  }
  .ds-select-wrap .ant-select-selection-item-content {
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    max-width: 300px !important;
  }
  .ds-select-wrap .ant-select-selection-item-remove {
    color: ${colors.textMuted} !important;
  }
  .ds-select-wrap .ant-select-selection-item-remove:hover {
    color: ${colors.textDefault} !important;
  }

  .ds-select-scrollbar::-webkit-scrollbar {
    width: 4px !important;
    height: 4px !important;
  }
  .ds-select-scrollbar::-webkit-scrollbar-track {
    background: transparent !important;
    border-radius: 2px !important;
  }
  .ds-select-scrollbar::-webkit-scrollbar-thumb {
    background: ${colors.surfaceDark} !important;
    border-radius: 2px !important;
  }
  .ds-select-scrollbar::-webkit-scrollbar-thumb:hover {
    background: ${colors.textMuted} !important;
  }
  .ds-select-scrollbar {
    scrollbar-width: thin !important;
    scrollbar-color: ${colors.surfaceDark} transparent !important;
  }
  /* Target Ant Design's internal scrollable container */
  .ds-select-scrollbar .rc-virtual-list-scrollbar {
    width: 4px !important;
  }
  .ds-select-scrollbar .rc-virtual-list-scrollbar-thumb {
    background: ${colors.surfaceDark} !important;
    border-radius: 2px !important;
  }
  .ds-select-scrollbar .ant-select-dropdown {
    scrollbar-width: thin !important;
  }
  .ds-select-scrollbar .ant-select-dropdown::-webkit-scrollbar {
    width: 4px !important;
    height: 4px !important;
  }

  /* Responsive dropdown styles for Ant Design Select */
  @media (max-width: 480px) {
    .ds-select-scrollbar .ant-select-item {
      padding: 6px 10px !important;
      font-size: 11px !important;
      min-height: 32px !important;
      line-height: 1.4 !important;
    }
    .ds-select-scrollbar .ant-select-item-option-content {
      font-size: 11px !important;
      line-height: 1.4 !important;
      width: 100% !important;
    }
    .ds-select-scrollbar .ant-select-item-option-content > div {
      gap: 8px !important;
      padding: 2px 0 !important;
      width: 100% !important;
    }
    .ds-select-scrollbar .ant-select-item-option-content .truncate {
      max-width: 120px !important;
      font-size: 11px !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
    }
    .ds-select-scrollbar .ant-select-item-option-content [class*="text-right"] {
      font-size: 9px !important;
      flex-shrink: 0 !important;
    }
    .ds-select-scrollbar {
      max-height: 220px !important;
      padding-bottom: 8px !important;
    }
    .ds-select-scrollbar .rc-virtual-list-holder {
      max-height: 210px !important;
    }
    /* Status badge smaller */
    .ds-select-scrollbar .ant-select-item-option-content [class*="Status"],
    .ds-select-scrollbar .ant-select-item-option-content span[class*="rounded"] {
      font-size: 9px !important;
      padding: 2px 6px !important;
      transform: scale(0.9);
      flex-shrink: 0 !important;
    }
  }
  @media (min-width: 481px) and (max-width: 768px) {
    .ds-select-scrollbar .ant-select-item {
      padding: 6px 12px !important;
      font-size: 12px !important;
      min-height: 36px !important;
      line-height: 1.4 !important;
    }
    .ds-select-scrollbar .ant-select-item-option-content {
      font-size: 12px !important;
      line-height: 1.4 !important;
      width: 100% !important;
    }
    .ds-select-scrollbar .ant-select-item-option-content > div {
      gap: 10px !important;
      padding: 2px 0 !important;
      width: 100% !important;
    }
    .ds-select-scrollbar .ant-select-item-option-content .truncate {
      max-width: 160px !important;
      font-size: 12px !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
    }
    .ds-select-scrollbar .ant-select-item-option-content [class*="text-right"] {
      font-size: 10px !important;
      flex-shrink: 0 !important;
    }
    .ds-select-scrollbar {
      max-height: 260px !important;
      padding-bottom: 8px !important;
    }
    .ds-select-scrollbar .rc-virtual-list-holder {
      max-height: 250px !important;
    }
  }
`;

// Hook to detect screen size
const useResponsiveSelect = () => {
  const [isMobile, setIsMobile] = React.useState(false);
  const [isTablet, setIsTablet] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const checkSize = () => {
      setIsMobile(window.innerWidth < 480);
      setIsTablet(window.innerWidth >= 480 && window.innerWidth < 768);
    };

    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  return { isMobile, isTablet };
};

export type SelectSize = "small" | "medium" | "large";
export type SelectVariant = "outlined" | "filled" | "borderless";

export interface SelectOption {
  value: string | number;
  label: React.ReactNode;
  disabled?: boolean;
}

export interface SelectGroupOption {
  label: React.ReactNode;
  options: SelectOption[];
}

export interface SearchFilterOption {
  value: string | number;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}
export interface SelectProps extends Omit<AntSelectProps, "size" | "options"> {
  label?: string;
  required?: boolean;
  helperText?: string;
  error?: string;
  size?: SelectSize;
  variant?: SelectVariant;
  fullWidth?: boolean;
  placeholder?: string;
  disabled?: boolean;
  value?: string | number | (string | number)[];
  defaultValue?: string | number | (string | number)[];
  onChange?: (value: any, option: any) => void;
  options?: readonly (SelectOption | SelectGroupOption)[];
  allowClear?: boolean;
  showSearch?: boolean;
  mode?: "multiple" | "tags";
  className?: string;
  /** Tooltip content for label */
  tooltip?: React.ReactNode;
  /** Tooltip placement */
  tooltipPlacement?: TooltipPlacement;
  /** Tooltip variant */
  tooltipVariant?: TooltipVariant;
  /** Allow tags to wrap to multiple lines (only for mode="multiple" or mode="tags") */
  wrapTags?: boolean;
}

const sizeConfig = {
  small: { height: 32, className: "h-8 text-sm" },
  medium: { height: 40, className: "h-10 text-sm" },
  large: { height: 48, className: "h-12 text-base" },
} as const;

// ============================================
// SearchInput Component (for dropdown search)
// ============================================

interface SearchInputProps {
  placeholder?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isMobile?: boolean;
}

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ placeholder = "Search", value, onChange, isMobile = false }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false);

    const getBorderColor = () => {
      if (isFocused) return colors.primary;
      return colors.surfaceSubtle;
    };

    return (
      <AntInput
        ref={ref as any}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        prefix={
          <Search
            className={isMobile ? "w-3.5 h-3.5" : "w-4 h-4"}
            style={{ color: colors.textMuted }}
          />
        }
        style={{
          height: isMobile ? 34 : 40,
          fontSize: isMobile ? "0.8125rem" : "0.875rem",
          borderRadius: 50,
          borderColor: getBorderColor(),
          borderWidth: isFocused ? 2 : 1,
          backgroundColor: colors.white,
          boxShadow: "none",
          transition: "border-color 0.15s ease",
        }}
      />
    );
  },
);

SearchInput.displayName = "SearchInput";

const sizeMap: Record<SelectSize, "small" | "middle" | "large"> = {
  small: "small",
  medium: "middle",
  large: "large",
};

export const Select: React.FC<SelectProps> = ({
  label,
  required = false,
  helperText,
  error,
  size = "medium",
  variant = "filled",
  fullWidth = true,
  placeholder,
  disabled = false,
  value,
  defaultValue,
  onChange,
  options,
  allowClear = false,
  showSearch = false,
  mode,
  className = "",
  style,
  onFocus,
  onBlur,
  tooltip,
  tooltipPlacement = "top",
  tooltipVariant = "default",
  wrapTags = false,
  ...props
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const hasValue = React.useMemo(() => {
    if (value !== undefined) {
      if (Array.isArray(value)) return value.length > 0;
      return value !== "" && value !== null;
    }
    if (defaultValue !== undefined) {
      if (Array.isArray(defaultValue)) return defaultValue.length > 0;
      return defaultValue !== "" && defaultValue !== null;
    }
    return false;
  }, [value, defaultValue]);

  const getBorderColor = () => {
    if (error) return colors.error;
    if (isFocused) return colors.primary;
    if (hasValue) return colors.surfaceDark;
    return colors.surfaceSubtle;
  };

  const getBackgroundColor = () => {
    if (disabled) return colors.surfaceLighter;
    if (variant === "filled") {
      return isFocused ? colors.white : colors.surfaceLight;
    }
    return colors.white;
  };

  const handleFocus = (e: React.FocusEvent<HTMLElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  return (
    <div className={cn(fullWidth && "w-full", className)}>
      <style>{scrollbarStyles}</style>
      {label && (
        <label
          className="inline-flex items-center gap-1 mb-1.5 text-sm font-medium"
          style={{ color: colors.textDefault }}
        >
          <span>{label}</span>
          {required && <span style={{ color: colors.error }}>*</span>}
          {tooltip && (
            <Tooltip
              title={tooltip}
              placement={tooltipPlacement}
              variant={tooltipVariant}
            >
              <span
                className="inline-flex items-center cursor-help"
                style={{ color: colors.textMuted }}
              >
                <Info size={14} />
              </span>
            </Tooltip>
          )}
        </label>
      )}

      <ConfigProvider
        theme={{
          token: {
            colorPrimary: colors.primary,
            colorError: colors.error,
            colorTextPlaceholder: colors.textMuted,
          },
          components: {
            Select: {
              controlHeight: sizeConfig[size].height,
              borderRadius: 3,
              optionSelectedBg: colors.primaryExtraLight,
              optionActiveBg: colors.surfaceLight,
            },
          },
        }}
      >
        <div
          className={cn(
            "flex items-center rounded-[3px] transition-all duration-150",
            !wrapTags && sizeConfig[size].className,
            disabled && "cursor-not-allowed opacity-60",
          )}
          style={{
            borderWidth: isFocused || error ? 2 : 1,
            borderStyle: "solid",
            borderColor: getBorderColor(),
            backgroundColor: getBackgroundColor(),
            ...(wrapTags && { minHeight: sizeConfig[size].height }),
          }}
        >
          <AntSelect
            placeholder={placeholder}
            disabled={disabled}
            value={value}
            defaultValue={defaultValue}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            options={options as (SelectOption | SelectGroupOption)[]}
            allowClear={allowClear}
            showSearch={showSearch}
            optionFilterProp="label"
            mode={mode}
            variant="borderless"
            size={sizeMap[size]}
            status={error ? "error" : undefined}
            className={cn(
              "w-full",
              mode && (wrapTags ? "ds-select-wrap" : "ds-select-nowrap"),
            )}
            classNames={{ popup: { root: "ds-select-scrollbar" } }}
            style={style}
            {...props}
          />
        </div>
      </ConfigProvider>

      {helperText && !error && (
        <div className="mt-1 text-xs" style={{ color: colors.textMuted }}>
          {helperText}
        </div>
      )}
      {error && (
        <div className="mt-1 text-xs" style={{ color: colors.error }}>
          {error}
        </div>
      )}
    </div>
  );
};

// ============================================
// SearchFilterSelect Component
// ============================================

export interface SearchFilterSelectProps {
  /** Label for the select */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Selected value */
  value?: string | number;
  /** Default value */
  defaultValue?: string | number;
  /** Options with label and description */
  options: SearchFilterOption[];
  /** Change handler */
  onChange?: (value: string | number, option: SearchFilterOption) => void;
  /** Size variant */
  size?: SelectSize;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Allow clearing selection */
  allowClear?: boolean;
  /** Whether to show search input in dropdown (default: true) */
  showSearch?: boolean;
  /** Search placeholder */
  searchPlaceholder?: string;
  /** Dropdown width (defaults to trigger width) */
  dropdownWidth?: number | string;
  /** Custom className */
  className?: string;
  /** Full width */
  fullWidth?: boolean;
  /** Dropdown max height */
  maxHeight?: number;
  /** Compact mode - reduces spacing for inline filter bars */
  compact?: boolean;
  /** Show radio-style indicator for each option (default: false) */
  showRadio?: boolean;
  /** Style variant - "default" (transparent) or "filled" (with background) */
  variant?: "default" | "filled";
  /** Custom color for the trigger label text and chevron. Defaults to primary in compact mode. */
  color?: string;
}

export const SearchFilterSelect: React.FC<SearchFilterSelectProps> = ({
  label,
  placeholder = "Select...",
  value,
  defaultValue,
  options,
  onChange,
  size = "medium",
  disabled = false,
  allowClear = false,
  showSearch = true,
  searchPlaceholder = "Search",
  dropdownWidth,
  className = "",
  fullWidth = false,
  maxHeight = 280,
  compact = false,
  showRadio = false,
  variant = "default",
  color,
}) => {
  const { isMobile, isTablet } = useResponsiveSelect();
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedValue, setSelectedValue] = React.useState<
    string | number | undefined
  >(value ?? defaultValue);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [dropdownDirection, setDropdownDirection] = React.useState<
    "above" | "below"
  >("below");
  const [constrainedMaxHeight, setConstrainedMaxHeight] = React.useState(maxHeight);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const optionRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  // Responsive max height
  const responsiveMaxHeight = isMobile ? 200 : isTablet ? 240 : maxHeight;

  // Calculate dropdown position when opening
  // Detects available space and opens above the trigger when there isn't enough room below
  const updateDropdownPosition = React.useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const gap = isMobile ? 4 : 6;
      const CHROME_HEIGHT = 80;

      // Use nearest dialog/modal as boundary, falling back to viewport
      const dialogEl = triggerRef.current.closest(
        '[role="dialog"]',
      ) as HTMLElement | null;
      const boundary = dialogEl
        ? dialogEl.getBoundingClientRect()
        : { top: 0, bottom: window.innerHeight };

      const spaceBelow = boundary.bottom - rect.bottom;
      const spaceAbove = rect.top - boundary.top;

      const openAbove =
        spaceBelow < responsiveMaxHeight + CHROME_HEIGHT &&
        spaceAbove > spaceBelow;

      // Constrain max height to available space within the boundary
      const availableSpace = openAbove ? spaceAbove : spaceBelow;
      setConstrainedMaxHeight(
        Math.max(
          120,
          Math.min(responsiveMaxHeight, availableSpace - CHROME_HEIGHT - gap),
        ),
      );

      setDropdownDirection(openAbove ? "above" : "below");
      setDropdownPosition({
        top: openAbove
          ? rect.top + window.scrollY - gap
          : rect.bottom + window.scrollY + gap,
        left: rect.left + window.scrollX,
        width: dropdownWidth
          ? typeof dropdownWidth === "number"
            ? dropdownWidth
            : parseInt(dropdownWidth)
          : Math.max(rect.width, isMobile ? 200 : 240),
      });
    }
  }, [dropdownWidth, isMobile, responsiveMaxHeight]);

  // Update position when dropdown opens
  React.useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      // Also update on scroll/resize
      window.addEventListener("scroll", updateDropdownPosition, true);
      window.addEventListener("resize", updateDropdownPosition);
      return () => {
        window.removeEventListener("scroll", updateDropdownPosition, true);
        window.removeEventListener("resize", updateDropdownPosition);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  // Sync with controlled value
  React.useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  // Filter options based on search
  const filteredOptions = React.useMemo(() => {
    if (!searchQuery) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        option.description?.toLowerCase().includes(query),
    );
  }, [options, searchQuery]);

  // Reset highlight when filtered list changes
  React.useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredOptions.length, searchQuery]);

  // Focus search input when dropdown opens
  React.useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Scroll highlighted item into view
  React.useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightedIndex]);

  // Close dropdown on outside click
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideContainer = containerRef.current?.contains(target);
      const isInsideDropdown = dropdownRef.current?.contains(target);

      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
        setSearchQuery("");
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get selected option
  const selectedOption = options.find((opt) => opt.value === selectedValue);

  // Handle option select
  const handleSelect = (option: SearchFilterOption) => {
    if (option.disabled) return;
    setSelectedValue(option.value);
    onChange?.(option.value, option);
    setIsOpen(false);
    setSearchQuery("");
    setHighlightedIndex(-1);
  };

  // Handle clear
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedValue(undefined);
    onChange?.("" as any, {} as SearchFilterOption);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1,
        );
        break;
      case "Enter":
        e.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredOptions.length
        ) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery("");
        setHighlightedIndex(-1);
        break;
    }
  };

  // Size configuration
  const triggerHeight = sizeConfig[size].height;

  return (
    <div
      ref={containerRef}
      className={cn("relative", fullWidth && "w-full", className)}
      onKeyDown={handleKeyDown}
    >
      <style>{scrollbarStyles}</style>
      {label && (
        <label
          className="block mb-1.5 text-sm font-medium"
          style={{ color: colors.textDefault }}
        >
          {label}
        </label>
      )}

      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={cn(
          "flex items-center focus:outline-none",
          variant === "filled"
            ? "rounded-lg px-3"
            : "bg-transparent border-none",
          compact ? "gap-1" : "gap-2 justify-between w-full",
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        )}
        style={{
          height: compact ? "auto" : triggerHeight,
          ...(variant === "filled" && {
            backgroundColor: colors.surfaceLighter,
            border: `1px solid ${colors.border}`,
          }),
        }}
      >
        <span
          className={cn(
            "truncate font-medium whitespace-nowrap",
            compact ? "text-sm" : "text-sm",
          )}
          style={{
            color: color ?? (compact ? colors.infoDark : colors.textDefault),
          }}
        >
          {selectedOption?.label || placeholder}
        </span>
        <div className={cn("flex items-center", compact ? "gap-0.5" : "gap-1")}>
          {allowClear && selectedValue && !disabled && (
            <X
              className={cn(
                "cursor-pointer hover:opacity-70 transition-opacity",
                compact ? "w-3 h-3" : "w-3.5 h-3.5",
              )}
              style={{ color: color ?? (compact ? colors.infoDark : colors.textMuted) }}
              onClick={handleClear}
            />
          )}
          <ChevronDown
            className={cn(
              "transition-transform duration-200",
              compact ? "w-4 h-4" : "w-4 h-4",
              isOpen && "rotate-180",
            )}
            style={{ color: color ?? (compact ? colors.infoDark : colors.textMuted) }}
          />
        </div>
      </button>

      {/* Dropdown - Rendered via Portal to escape overflow:hidden */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              // Only apply default minWidth if dropdownWidth is not explicitly set
              ...(dropdownWidth ? {} : { minWidth: isMobile ? 200 : 240 }),
              zIndex: 99999,
              ...(dropdownDirection === "above" && {
                transform: "translateY(-100%)",
              }),
            }}
          >
            {/* Arrow/Pointer — points up when dropdown is below, down when above */}
            {/* zIndex is intentionally omitted so the content box covers the inner half */}
            {dropdownDirection === "below" ? (
              <div
                className={cn(
                  "absolute left-8",
                  isMobile ? "-top-[6px]" : "-top-[7px]",
                )}
                style={{
                  width: isMobile ? 12 : 14,
                  height: isMobile ? 12 : 14,
                  backgroundColor: colors.white,
                  borderLeft: `1px solid ${colors.surfaceSubtle}`,
                  borderTop: `1px solid ${colors.surfaceSubtle}`,
                  transform: "rotate(45deg)",
                  borderTopLeftRadius: 3,
                }}
              />
            ) : (
              <div
                className={cn(
                  "absolute left-8",
                  isMobile ? "-bottom-[6px]" : "-bottom-[7px]",
                )}
                style={{
                  width: isMobile ? 12 : 14,
                  height: isMobile ? 12 : 14,
                  backgroundColor: colors.white,
                  borderRight: `1px solid ${colors.surfaceSubtle}`,
                  borderBottom: `1px solid ${colors.surfaceSubtle}`,
                  transform: "rotate(45deg)",
                  borderBottomRightRadius: 3,
                }}
              />
            )}
            {/* Dropdown Content — relative + z-index so it covers the inner half of the arrow */}
            <div
              className="relative overflow-hidden"
              style={{
                zIndex: 1,
                backgroundColor: colors.white,
                border: `1px solid ${colors.surfaceSubtle}`,
                borderRadius: isMobile ? 8 : 12,
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
              }}
            >
              {/* Search Input - conditionally rendered */}
              {showSearch && (
                <>
                  <div
                    className={cn(isMobile ? "px-3 py-2" : "px-4 py-3")}
                    style={{ backgroundColor: colors.surfaceLightest }}
                  >
                    <SearchInput
                      ref={searchInputRef}
                      placeholder={searchPlaceholder}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      isMobile={isMobile}
                    />
                  </div>

                  {/* Divider */}
                  <div
                    className="h-px w-full"
                    style={{ backgroundColor: colors.surfaceSubtle }}
                  />
                </>
              )}

              {/* Options List */}
              <div
                className="overflow-y-auto overflow-x-auto py-1 ds-select-scrollbar"
                style={{ maxHeight: constrainedMaxHeight }}
              >
                {filteredOptions.length === 0 ? (
                  <div
                    className={cn(
                      "text-center",
                      isMobile ? "py-4 px-3 text-xs" : "py-6 px-4 text-sm",
                    )}
                    style={{ color: colors.textMuted }}
                  >
                    No results found
                  </div>
                ) : (
                  filteredOptions.map((option, index) => {
                    const isSelected = option.value === selectedValue;
                    const isHighlighted = index === highlightedIndex;

                    return (
                      <div
                        key={option.value}
                        ref={(el) => {
                          optionRefs.current[index] = el;
                        }}
                        className={cn(
                          "cursor-pointer transition-colors duration-150",
                          isMobile ? "px-3 py-2" : "px-4 py-2.5",
                          option.disabled && "cursor-not-allowed opacity-50",
                        )}
                        style={{
                          backgroundColor: isHighlighted
                            ? colors.primaryExtraLight
                            : "transparent",
                        }}
                        onMouseEnter={() =>
                          !option.disabled && setHighlightedIndex(index)
                        }
                        onMouseLeave={() => {
                          if (highlightedIndex === index)
                            setHighlightedIndex(-1);
                        }}
                        onClick={() => handleSelect(option)}
                      >
                        <div
                          className={cn(
                            "flex items-center",
                            showRadio
                              ? (isMobile ? "gap-2" : "gap-3")
                              : "gap-2",
                          )}
                        >
                          {showRadio && (
                            <div
                              className="flex-shrink-0 flex items-center justify-center rounded-full border-2 transition-all duration-150"
                              style={{
                                width: isMobile ? 16 : 18,
                                height: isMobile ? 16 : 18,
                                borderColor: isSelected
                                  ? colors.primary
                                  : colors.surfaceDark,
                                backgroundColor: isSelected
                                  ? colors.primary
                                  : "transparent",
                              }}
                            >
                              {isSelected && (
                                <Check
                                  className={isMobile ? "w-2.5 h-2.5" : "w-3 h-3"}
                                  style={{ color: colors.white }}
                                  strokeWidth={3}
                                />
                              )}
                            </div>
                          )}
                          {option.icon && (
                            <span style={{ color: colors.textMuted }}>
                              {option.icon}
                            </span>
                          )}
                          <div className="flex-1 min-w-0">
                            <div
                              className={isMobile ? "text-xs" : "text-sm"}
                              style={{
                                color: isSelected
                                  ? colors.primary
                                  : colors.textDefault,
                                fontWeight: isSelected ? 600 : 400,
                              }}
                            >
                              {option.label}
                            </div>
                            {option.description && (
                              <div
                                className={cn(
                                  "mt-0.5",
                                  isMobile ? "text-[10px]" : "text-xs",
                                )}
                                style={{ color: colors.textMuted }}
                              >
                                {option.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

// ============================================
// MultiSelectCheckbox Component
// ============================================

export interface MultiSelectCheckboxOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface MultiSelectCheckboxProps {
  /** Label for the select */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Selected values */
  value?: (string | number)[];
  /** Default values */
  defaultValue?: (string | number)[];
  /** Options */
  options: MultiSelectCheckboxOption[];
  /** Change handler */
  onChange?: (values: (string | number)[]) => void;
  /** Size variant */
  size?: SelectSize;
  /** Visual variant */
  variant?: SelectVariant;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Allow clearing selection */
  allowClear?: boolean;
  /** Whether to show search input in dropdown (default: true) */
  showSearch?: boolean;
  /** Search placeholder */
  searchPlaceholder?: string;
  /** Dropdown width (defaults to trigger width) */
  dropdownWidth?: number | string;
  /** Custom className */
  className?: string;
  /** Full width */
  fullWidth?: boolean;
  /** Dropdown max height */
  maxHeight?: number;
  /** Error message */
  error?: string;
  /** Required field */
  required?: boolean;
  /** Callback for server-side search. When provided, client-side filtering is bypassed. */
  onSearch?: (query: string) => void;
  /** Close dropdown when mouse leaves (useful for multi-select in modals) */
  closeOnMouseLeave?: boolean;
  /** Callback when dropdown closes */
  onDropdownClose?: () => void;
}

export const MultiSelectCheckbox: React.FC<MultiSelectCheckboxProps> = ({
  label,
  placeholder = "Search",
  value,
  defaultValue,
  options,
  onChange,
  size = "medium",
  variant = "outlined",
  disabled = false,
  allowClear = false,
  showSearch = true,
  searchPlaceholder = "Search",
  dropdownWidth,
  className = "",
  fullWidth = true,
  closeOnMouseLeave = false,
  onDropdownClose,
  maxHeight = 280,
  error,
  required = false,
  onSearch,
}) => {
  const { isMobile, isTablet } = useResponsiveSelect();
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedValues, setSelectedValues] = React.useState<
    (string | number)[]
  >(value ?? defaultValue ?? []);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [dropdownDirection, setDropdownDirection] = React.useState<
    "above" | "below"
  >("below");
  const [constrainedMaxHeight, setConstrainedMaxHeight] = React.useState(maxHeight);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const optionRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  // Responsive max height
  const responsiveMaxHeight = isMobile ? 200 : isTablet ? 240 : maxHeight;

  // Calculate dropdown position when opening
  const updateDropdownPosition = React.useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const gap = isMobile ? 4 : 6;
      const CHROME_HEIGHT = 80;

      // Use nearest dialog/modal as boundary, falling back to viewport
      const dialogEl = triggerRef.current.closest(
        '[role="dialog"]',
      ) as HTMLElement | null;
      const boundary = dialogEl
        ? dialogEl.getBoundingClientRect()
        : { top: 0, bottom: window.innerHeight };

      const spaceBelow = boundary.bottom - rect.bottom;
      const spaceAbove = rect.top - boundary.top;

      const openAbove =
        spaceBelow < responsiveMaxHeight + CHROME_HEIGHT &&
        spaceAbove > spaceBelow;

      // Constrain max height to available space within the boundary
      const availableSpace = openAbove ? spaceAbove : spaceBelow;
      setConstrainedMaxHeight(
        Math.max(
          120,
          Math.min(responsiveMaxHeight, availableSpace - CHROME_HEIGHT - gap),
        ),
      );

      setDropdownDirection(openAbove ? "above" : "below");
      setDropdownPosition({
        top: openAbove
          ? rect.top + window.scrollY - gap
          : rect.bottom + window.scrollY + gap,
        left: rect.left + window.scrollX,
        width: dropdownWidth
          ? typeof dropdownWidth === "number"
            ? dropdownWidth
            : parseInt(dropdownWidth)
          : Math.max(rect.width, isMobile ? 280 : 320),
      });
    }
  }, [dropdownWidth, isMobile, responsiveMaxHeight]);

  // Update position when dropdown opens
  React.useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener("scroll", updateDropdownPosition, true);
      window.addEventListener("resize", updateDropdownPosition);
      return () => {
        window.removeEventListener("scroll", updateDropdownPosition, true);
        window.removeEventListener("resize", updateDropdownPosition);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  // Sync with controlled value
  React.useEffect(() => {
    if (value !== undefined) {
      setSelectedValues(value);
    }
  }, [value]);

  // Filter options based on search (skip when onSearch handles it server-side)
  const filteredOptions = React.useMemo(() => {
    if (onSearch || !searchQuery) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(query),
    );
  }, [options, searchQuery, onSearch]);

  // Reset highlight when filtered list changes
  React.useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredOptions.length, searchQuery]);

  // Focus search input when dropdown opens
  React.useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Scroll highlighted item into view
  React.useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightedIndex]);

  // Close dropdown on outside click
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideContainer = containerRef.current?.contains(target);
      const isInsideDropdown = dropdownRef.current?.contains(target);

      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
        setSearchQuery("");
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle option toggle
  const handleToggle = (
    optionValue: string | number,
    optionDisabled?: boolean,
  ) => {
    if (optionDisabled) return;
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue];
    setSelectedValues(newValues);
    onChange?.(newValues);
  };

  // Handle clear all
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedValues([]);
    onChange?.([]);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1,
        );
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredOptions.length
        ) {
          handleToggle(
            filteredOptions[highlightedIndex].value,
            filteredOptions[highlightedIndex].disabled,
          );
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery("");
        setHighlightedIndex(-1);
        break;
    }
  };

  // Get display text
  const displayText = React.useMemo(() => {
    if (selectedValues.length === 0) return placeholder;
    const selectedLabels = options
      .filter((opt) => selectedValues.includes(opt.value))
      .map((opt) => opt.label);
    if (selectedLabels.length <= 2) {
      return selectedLabels.join(", ");
    }
    return `${selectedLabels.slice(0, 2).join(", ")} +${selectedLabels.length - 2}`;
  }, [selectedValues, options, placeholder]);

  // Size configuration
  const triggerHeight = sizeConfig[size].height;

  return (
    <div
      ref={containerRef}
      className={cn("relative", fullWidth && "w-full", className)}
      onKeyDown={handleKeyDown}
    >
      <style>{scrollbarStyles}</style>
      {label && (
        <label
          className="block mb-1.5 text-sm font-medium"
          style={{ color: colors.textDefault }}
        >
          {label}
          {required && <span style={{ color: colors.error }}> *</span>}
        </label>
      )}

      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={cn(
          "flex items-center justify-between w-full px-3 rounded-[3px] transition-all duration-150",
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        )}
        style={{
          height: triggerHeight,
          borderWidth: variant === "borderless" ? 0 : isOpen || error ? 2 : 1,
          borderStyle: "solid",
          borderColor:
            variant === "borderless"
              ? "transparent"
              : error
                ? colors.error
                : isOpen
                  ? colors.primary
                  : selectedValues.length > 0
                    ? colors.surfaceDark
                    : colors.surfaceSubtle,
          backgroundColor:
            variant === "borderless"
              ? "transparent"
              : disabled
                ? colors.surfaceLighter
                : variant === "filled"
                  ? isOpen
                    ? colors.white
                    : colors.surfaceLight
                  : colors.white,
        }}
      >
        <span
          className="truncate text-sm"
          style={{
            color:
              selectedValues.length > 0 ? colors.textDefault : colors.textMuted,
          }}
        >
          {displayText}
        </span>
        <div className="flex items-center gap-1">
          {allowClear && selectedValues.length > 0 && !disabled && (
            <X
              className="w-4 h-4 cursor-pointer hover:opacity-70 transition-opacity"
              style={{ color: colors.textMuted }}
              onClick={handleClear}
            />
          )}
          <ChevronDown
            className={cn(
              "w-4 h-4 transition-transform duration-200",
              isOpen && "rotate-180",
            )}
            style={{ color: colors.textMuted }}
          />
        </div>
      </button>

      {error && (
        <div className="mt-1 text-xs" style={{ color: colors.error }}>
          {error}
        </div>
      )}

      {/* Dropdown - Rendered via Portal */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              zIndex: 99999,
              ...(dropdownDirection === "above" && {
                transform: "translateY(-100%)",
              }),
            }}
            onMouseLeave={closeOnMouseLeave ? () => {
              setIsOpen(false);
              setSearchQuery("");
              setHighlightedIndex(-1);
              onDropdownClose?.();
            } : undefined}
          >
            {/* Dropdown Content */}
            <div
              className="relative overflow-hidden"
              style={{
                backgroundColor: colors.white,
                border: `1px solid ${colors.surfaceSubtle}`,
                borderRadius: 4,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              }}
            >
              {/* Search Input */}
              {showSearch && (
                <div
                  className="p-3"
                  style={{ borderBottom: `1px solid ${colors.surfaceSubtle}` }}
                >
                  <Input
                    ref={searchInputRef as React.Ref<any>}
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      onSearch?.(e.target.value);
                    }}
                    size="small"
                    variant="filled"
                    fullWidth
                    suffix={
                      <Search
                        className="w-4 h-4"
                        style={{ color: colors.textMuted }}
                      />
                    }
                  />
                </div>
              )}

              {/* Options List */}
              <div
                className="overflow-y-auto ds-select-scrollbar"
                style={{ maxHeight: constrainedMaxHeight }}
              >
                {filteredOptions.length === 0 ? (
                  <div
                    className="text-center py-6 px-4 text-sm"
                    style={{ color: colors.textMuted }}
                  >
                    No results found
                  </div>
                ) : (
                  filteredOptions.map((option, index) => {
                    const isSelected = selectedValues.includes(option.value);
                    const isHighlighted = index === highlightedIndex;

                    return (
                      <div
                        key={option.value}
                        ref={(el) => {
                          optionRefs.current[index] = el;
                        }}
                        className={cn(
                          "cursor-pointer transition-colors duration-150",
                          option.disabled && "cursor-not-allowed opacity-50",
                        )}
                        style={{
                          backgroundColor: isHighlighted
                            ? colors.primaryExtraLight
                            : "transparent",
                          borderLeft: isHighlighted
                            ? `3px solid ${colors.primary}`
                            : "3px solid transparent",
                        }}
                        onMouseEnter={() =>
                          !option.disabled && setHighlightedIndex(index)
                        }
                        onMouseLeave={() => {
                          if (highlightedIndex === index)
                            setHighlightedIndex(-1);
                        }}
                        onClick={() =>
                          handleToggle(option.value, option.disabled)
                        }
                      >
                        <div className="flex items-center gap-3 px-3 py-2.5">
                          <Checkbox
                            checked={isSelected}
                            size="small"
                            variant="default"
                            disabled={option.disabled}
                            onChange={() => {}}
                          />
                          <span
                            className="text-sm flex-1"
                            style={{ color: colors.textDefault }}
                          >
                            {option.label}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

// ============================================
// TimeSelect Component
// Time dropdown with 30-minute intervals and timezone
// ============================================

export interface TimeSelectOption {
  value: string; // 24-hour format: "00:00", "00:30", "01:00", etc.
  label: string; // 12-hour format with timezone: "12:00 AM GMT+5:30"
}

export interface TimeSelectProps {
  /** Label for the select */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Selected value in 24-hour format (HH:mm or HH:mm:ss) */
  value?: string;
  /** Change handler - returns 24-hour format */
  onChange?: (value: string) => void;
  /** Size variant */
  size?: SelectSize;
  /** Visual variant */
  variant?: SelectVariant;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Custom className */
  className?: string;
  /** Full width */
  fullWidth?: boolean;
  /** Error state */
  error?: string;
  /** Interval in minutes (default: 30) */
  interval?: number;
  /** Show timezone in options */
  showTimezone?: boolean;
}

// Helper to get user's timezone offset string (e.g., "GMT+5:30")
const getTimezoneString = (): string => {
  const offset = -new Date().getTimezoneOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset >= 0 ? "+" : "-";
  if (minutes === 0) {
    return `GMT${sign}${hours}`;
  }
  return `GMT${sign}${hours}:${minutes.toString().padStart(2, "0")}`;
};

// Helper to convert 24-hour time to 12-hour format with AM/PM
const formatTime12Hour = (hours: number, minutes: number): string => {
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
};

// Generate time options
const generateTimeOptions = (
  interval: number = 30,
  showTimezone: boolean = true
): TimeSelectOption[] => {
  const options: TimeSelectOption[] = [];
  const timezone = showTimezone ? ` ${getTimezoneString()}` : "";

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += interval) {
      const value = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      const label = `${formatTime12Hour(hour, minute)}${timezone}`;
      options.push({ value, label });
    }
  }

  return options;
};

export const TimeSelect: React.FC<TimeSelectProps> = ({
  label,
  placeholder = "HH:MM",
  value,
  onChange,
  size = "medium",
  variant = "filled",
  disabled = false,
  className = "",
  fullWidth = true,
  error,
  interval = 30,
  showTimezone = true,
}) => {
  const { isMobile, isTablet } = useResponsiveSelect();
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [dropdownDirection, setDropdownDirection] = React.useState<
    "above" | "below"
  >("below");
  const responsiveMaxHeight = isMobile ? 200 : isTablet ? 240 : 280;
  const [constrainedMaxHeight, setConstrainedMaxHeight] =
    React.useState(responsiveMaxHeight);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const optionRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  // Generate time options
  const timeOptions = React.useMemo(
    () => generateTimeOptions(interval, showTimezone),
    [interval, showTimezone]
  );

  // Parse value to get just HH:mm
  const normalizedValue = React.useMemo(() => {
    if (!value) return "";
    // Handle HH:mm:ss format
    const match = value.match(/^(\d{2}):(\d{2})/);
    if (match) {
      return `${match[1]}:${match[2]}`;
    }
    return value;
  }, [value]);

  // Get selected option
  const selectedOption = React.useMemo(
    () => timeOptions.find((opt) => opt.value === normalizedValue),
    [timeOptions, normalizedValue]
  );

  // Filter options based on search
  const filteredOptions = React.useMemo(() => {
    if (!searchQuery) return timeOptions;
    const query = searchQuery.toLowerCase();
    return timeOptions.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [timeOptions, searchQuery]);

  // Calculate dropdown position
  const updateDropdownPosition = React.useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const gap = 4;
      const CHROME_HEIGHT = 80;

      // Use nearest dialog/modal as boundary, falling back to viewport
      const dialogEl = triggerRef.current.closest(
        '[role="dialog"]',
      ) as HTMLElement | null;
      const boundary = dialogEl
        ? dialogEl.getBoundingClientRect()
        : { top: 0, bottom: window.innerHeight };

      const spaceBelow = boundary.bottom - rect.bottom;
      const spaceAbove = rect.top - boundary.top;

      const openAbove =
        spaceBelow < responsiveMaxHeight + CHROME_HEIGHT &&
        spaceAbove > spaceBelow;

      // Constrain max height to available space within the boundary
      const availableSpace = openAbove ? spaceAbove : spaceBelow;
      setConstrainedMaxHeight(
        Math.max(
          120,
          Math.min(responsiveMaxHeight, availableSpace - CHROME_HEIGHT - gap),
        ),
      );

      setDropdownDirection(openAbove ? "above" : "below");
      setDropdownPosition({
        top: openAbove
          ? rect.top + window.scrollY - gap
          : rect.bottom + window.scrollY + gap,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, isMobile ? 200 : 220),
      });
    }
  }, [isMobile, responsiveMaxHeight]);

  // Update position when dropdown opens
  React.useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener("scroll", updateDropdownPosition, true);
      window.addEventListener("resize", updateDropdownPosition);
      return () => {
        window.removeEventListener("scroll", updateDropdownPosition, true);
        window.removeEventListener("resize", updateDropdownPosition);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  // Scroll to selected option when dropdown opens
  React.useEffect(() => {
    if (isOpen && selectedOption) {
      const selectedIndex = filteredOptions.findIndex(
        (opt) => opt.value === selectedOption.value
      );
      if (selectedIndex >= 0 && optionRefs.current[selectedIndex]) {
        setTimeout(() => {
          optionRefs.current[selectedIndex]?.scrollIntoView({
            block: "center",
            behavior: "auto",
          });
        }, 50);
      }
    }
  }, [isOpen, selectedOption, filteredOptions]);

  // Focus search input when dropdown opens
  React.useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Close dropdown on outside click
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideContainer = containerRef.current?.contains(target);
      const isInsideDropdown = dropdownRef.current?.contains(target);

      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
        setSearchQuery("");
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle option select
  const handleSelect = (option: TimeSelectOption) => {
    onChange?.(option.value + ":00"); // Add seconds for consistency
    setIsOpen(false);
    setSearchQuery("");
    setHighlightedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery("");
        setHighlightedIndex(-1);
        break;
    }
  };

  // Scroll highlighted item into view
  React.useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightedIndex]);

  const triggerHeight = sizeConfig[size].height;

  return (
    <div
      ref={containerRef}
      className={cn("relative", fullWidth && "w-full", className)}
      onKeyDown={handleKeyDown}
    >
      <style>{scrollbarStyles}</style>
      {label && (
        <label
          className="block mb-1.5 text-sm font-medium"
          style={{ color: colors.textDefault }}
        >
          {label}
        </label>
      )}

      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={cn(
          "flex items-center justify-between w-full px-3 rounded-[3px] transition-all duration-150",
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
        )}
        style={{
          height: triggerHeight,
          borderWidth: variant === "borderless" ? 0 : isOpen || error ? 2 : 1,
          borderStyle: "solid",
          borderColor:
            variant === "borderless"
              ? "transparent"
              : error
                ? colors.error
                : isOpen
                  ? colors.primary
                  : selectedOption
                    ? colors.surfaceDark
                    : colors.surfaceSubtle,
          backgroundColor:
            variant === "borderless"
              ? "transparent"
              : disabled
                ? colors.surfaceLighter
                : variant === "filled"
                  ? isOpen
                    ? colors.white
                    : colors.surfaceLight
                  : colors.white,
        }}
      >
        <span
          className="truncate text-sm"
          style={{
            color: selectedOption ? colors.textDefault : colors.textMuted,
          }}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          className={cn(
            "w-4 h-4 transition-transform duration-200",
            isOpen && "rotate-180"
          )}
          style={{ color: colors.textMuted }}
        />
      </button>

      {error && (
        <div className="mt-1 text-xs" style={{ color: colors.error }}>
          {error}
        </div>
      )}

      {/* Dropdown - Rendered via Portal */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              zIndex: 99999,
              ...(dropdownDirection === "above"
                ? { transform: "translateY(-100%)" }
                : {}),
            }}
          >
            {/* Dropdown Content */}
            <div
              className="relative overflow-hidden"
              style={{
                backgroundColor: colors.white,
                border: `1px solid ${colors.surfaceSubtle}`,
                borderRadius: 4,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              }}
            >
              {/* Search Input */}
              <div
                className="p-2"
                style={{ borderBottom: `1px solid ${colors.surfaceSubtle}` }}
              >
                <Input
                  ref={searchInputRef as React.Ref<any>}
                  type="text"
                  placeholder="Search time..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  variant="filled"
                  fullWidth
                  suffix={
                    <Search
                      className="w-4 h-4"
                      style={{ color: colors.textMuted }}
                    />
                  }
                />
              </div>

              {/* Options List */}
              <div
                className="overflow-y-auto ds-select-scrollbar"
                style={{ maxHeight: constrainedMaxHeight }}
              >
                {filteredOptions.length === 0 ? (
                  <div
                    className="text-center py-4 px-3 text-sm"
                    style={{ color: colors.textMuted }}
                  >
                    No results found
                  </div>
                ) : (
                  filteredOptions.map((option, index) => {
                    const isSelected = option.value === normalizedValue;
                    const isHighlighted = index === highlightedIndex;

                    return (
                      <div
                        key={option.value}
                        ref={(el) => {
                          optionRefs.current[index] = el;
                        }}
                        className="cursor-pointer transition-colors duration-150 px-3 py-2"
                        style={{
                          backgroundColor: isSelected
                            ? colors.primaryExtraLight
                            : isHighlighted
                              ? colors.surfaceLight
                              : "transparent",
                        }}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        onMouseLeave={() => {
                          if (highlightedIndex === index) setHighlightedIndex(-1);
                        }}
                        onClick={() => handleSelect(option)}
                      >
                        <span
                          className="text-sm"
                          style={{
                            color: isSelected ? colors.primary : colors.textDefault,
                            fontWeight: isSelected ? 600 : 400,
                          }}
                        >
                          {option.label}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Select;
